import React from "react";
import {Link} from "react-router-dom";
import {IoIosArrowForward} from "react-icons/io";
import {TbBrandGithubFilled} from "react-icons/tb";
import {Tags, useScrollToHash} from "../projectBits";
import banner from "../../assets/projects/earthPlace/banner.jpg";
import pond from "../../assets/projects/earthPlace/pond.jpg";
import riverside from "../../assets/projects/earthPlace/riverside.jpg";
import quest from "../../assets/projects/earthPlace/quest.jpg";
import buildTools from "../../assets/projects/earthPlace/build-tools.jpg";
import layers from "../../assets/projects/earthPlace/layers.jpg";
import dusk from "../../assets/projects/earthPlace/dusk.jpg";
import map from "../../assets/projects/earthPlace/map.jpg";
import {EditorBar} from "./editorLink";

const REPO = "https://github.com/nour2063/earthPlace";
const TESTFLIGHT = "https://testflight.apple.com/join/fPfRkrbH";
const ANDROID_BETA = "https://appdistribution.firebase.dev/i/0f55bd682b2cd0d3";
const QUEST_PREVIEW = "https://appdistribution.firebase.dev/i/78058d8183d1bf46";

interface Platform {
    name: string;
    steps: React.ReactNode[];
    requirements: string;
    link?: string;
    linkLabel: string;
}

const PLATFORMS: Platform[] = [
    {
        name: "iOS beta",
        steps: [<>Install <b>TestFlight</b> from the App Store</>, "Open the invite on your iPhone or iPad", "Allow camera & location, then head outside"],
        requirements: "iOS 15+ on an ARKit device, LiDAR recommended",
        link: TESTFLIGHT,
        linkLabel: "Join on TestFlight",
    },
    {
        name: "Android beta",
        steps: ["Open the invite on your phone", <>Install through <b>Firebase App Distribution</b></>, "Allow camera & location, then head outside"],
        requirements: "Android 7.0+ on an ARCore device",
        link: ANDROID_BETA,
        linkLabel: "Get the beta",
    },
    {
        name: "Meta Quest preview",
        steps: ["Open the invite on your phone or PC", <>Install on your Quest 3 through <b>Firebase App Distribution</b></>, "Pick a layer on the map and join it"],
        requirements: "A lighter build: joins existing layers only, with no scanning, location or blueprints",
        link: QUEST_PREVIEW,
        linkLabel: "Get the preview",
    },
];

const GALLERY = [
    {src: riverside, title: "Anchored on the riverside", caption: "Voxels placed and left along the water."},
    {src: quest, title: "The same world on a Quest", caption: "Building the shared space in passthrough."},
    {src: buildTools, title: "Build tools in the phone app", caption: "Colour palette, place, erase, and capture."},
    {src: map, title: "Discover builds around you", caption: "Layers are pinned on the map where they were made."},
    {src: layers, title: "World layers you can join", caption: "Each place is mapped, saved, and shareable."},
    {src: dusk, title: "A crafted asset at dusk", caption: "Detailed blueprint placed on a trail."},
];

export function EarthPlacePage() {
    useScrollToHash(true);
    return (
        <div className="pj-page">
            <EditorBar/>
            <img className="pj-banner" src={banner} alt="Voxel letters spelling Glasgow above a hedge"/>
            <div className="pj-page-head">
                <div>
                    <h2>earthPlace</h2>
                    <p className="pj-muted">World-locked AR voxel art builder</p>
                </div>
                <Tags tags={["Unity", "iOS · Android · Quest 3"]}/>
            </div>
            <hr/>
            <p>
                earthPlace is a real-time collaborative art tool that lets people decorate public space in augmented reality. Inspired by r/place and Wplace, it lets anyone draw and build in 3D, with every edit anchored and kept persistent to a real-world location. The same shared world opens on a phone or a Meta Quest headset, and I ran it with real users in the wild to see how the device in someone's hands changes how they build.
            </p>
            <figure className="pj-figure">
                <img className="pj-wide" src={pond} alt="Green voxel lily pads floating on a pond"/>
                <figcaption className="pj-muted">Builds stay saved to the exact spot where they were made, ready for the next person who visits.</figcaption>
            </figure>

            <h3 id="try-it-out">Try it out</h3>
            <hr/>
            <div className="ep-platforms">
                {PLATFORMS.map(p => (
                    <div key={p.name} className="ep-platform">
                        <h4>{p.name}</h4>
                        <ol>{p.steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
                        <p className="pj-small pj-muted">{p.requirements}</p>
                        {p.link && (
                            <a className="button pj-act" href={p.link} target="_blank" rel="noopener noreferrer">
                                {p.linkLabel} <IoIosArrowForward/>
                            </a>
                        )}
                    </div>
                ))}
            </div>

            <h3>Gallery</h3>
            <hr/>
            <div className="ep-gallery">
                {GALLERY.map(g => (
                    <figure key={g.title}>
                        <img src={g.src} alt={g.title} loading="lazy"/>
                        <figcaption><b>{g.title}</b><span className="pj-muted">{g.caption}</span></figcaption>
                    </figure>
                ))}
            </div>

            <div className="ep-feedback">
                <div>
                    <h4>Found a bug or have an idea?</h4>
                    <p className="pj-small pj-muted">Reports and feature requests live on GitHub</p>
                </div>
                <div className="pj-acts">
                    <a className="button pj-act" href={`${REPO}/issues/new?template=bug_report.yml`} target="_blank" rel="noopener noreferrer">
                        <TbBrandGithubFilled/> Report a bug
                    </a>
                    <a className="button pj-act" href={`${REPO}/issues/new?template=feature_request.yml`} target="_blank" rel="noopener noreferrer">
                        <TbBrandGithubFilled/> Request a feature
                    </a>
                </div>
            </div>
            <div className="ep-footer">
                <Link to="/projects/earthplace/privacy">Privacy policy</Link>
                <Link to="/projects/earthplace/support">Support</Link>
                <span className="pj-faint">© 2026 Nour Elfangary · earthPlace™</span>
            </div>
        </div>
    );
}
